import React, { useEffect, useReducer } from "react";
import { motion } from "framer-motion";
import Cookies from "js-cookie";

import { Business } from "../services/queries/businesses/types";
import { User, UserRole } from "../services/queries/users/types";
import AppSkeleton from "../components/Skeleton/AppSkeleton";
import { token } from "../constants";
import { getUserRoleFromToken } from "../utils/user";

type AppState = {
  authLoading: boolean;
  authToken: string;
  businessUserRole?: UserRole;
  user: User | null;
  business: Business | null;
  orderDetails: Order | null;
  businessOrderId: string | null;
  activeSendFormScreen: string;
};

type Order = {
  baseAmount: string;
  quoteAmount: string;
  baseCurrency: string;
  quoteCurrency: string;
  rateId?: string;
};

export type AppContextValue = {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
};

interface Props {
  children: React.ReactNode;
}

export const AUTH_LOADING = "AUTH_LOADING";
export const SAVE_AUTH_TOKEN = "SAVE_AUTH_TOKEN";
export const SAVE_USER = "SAVE_USER";
export const SAVE_BUSINESS = "SAVE_BUSINESS";
export const LOGOUT = "LOGOUT";
export const SAVE_ORDER_DETAILS = "SAVE_ORDER_DETAILS";
export const SAVE_BUSINESS_ORDER_ID = "SAVE_BUSINESS_ORDER_ID";
export const RESTORE_SESSION = "RESTORE_SESSION";
export const RESET_ORDER_DETAILS = "RESET_ORDER_DETAILS";
export const UPDATE_SEND_FORM_SECTION = "UPDATE_SEND_FORM_SECTION";

type Session = {
  authToken: string;
  user: User;
  business: Business;
  businessUserRole: UserRole;
};

type AppAction =
  | { type: typeof AUTH_LOADING; payload: boolean }
  | { type: typeof SAVE_AUTH_TOKEN; payload: string }
  | { type: typeof SAVE_USER; payload: User }
  | { type: typeof SAVE_BUSINESS; payload: Business }
  | { type: typeof RESTORE_SESSION; payload: Session }
  | { type: typeof LOGOUT }
  | {
      type: typeof SAVE_ORDER_DETAILS;
      payload: Order;
    }
  | {
      type: typeof SAVE_BUSINESS_ORDER_ID;
      payload: string;
    }
  | {
      type: typeof RESET_ORDER_DETAILS;
    }
  | { type: typeof UPDATE_SEND_FORM_SECTION; payload: string };

const initialState: AppState = {
  authLoading: true,
  authToken: "",
  businessUserRole: "" as UserRole,
  user: null,
  business: null,
  orderDetails: null,
  businessOrderId: null,
  activeSendFormScreen: "amountInput",
};

function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case AUTH_LOADING:
      return { ...state, authLoading: action.payload };

    case SAVE_AUTH_TOKEN:
      return { ...state, authToken: action.payload };

    case SAVE_USER:
      localStorage.setItem("user", JSON.stringify(action.payload));

      return { ...state, user: action.payload, authLoading: false };

    case SAVE_BUSINESS:
      localStorage.setItem("business", JSON.stringify(action.payload));

      return { ...state, business: action.payload };

    case RESTORE_SESSION:
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      localStorage.setItem("business", JSON.stringify(action.payload.business));

      return { ...state, ...action.payload, authLoading: false };

    case SAVE_ORDER_DETAILS:
      return { ...state, orderDetails: action.payload };

    case RESET_ORDER_DETAILS:
      return {
        ...state,
        orderDetails: null,
        activeSendFormScreen: "amountInput",
      };

    case SAVE_BUSINESS_ORDER_ID:
      return { ...state, businessOrderId: action.payload };

    case UPDATE_SEND_FORM_SECTION:
      return { ...state, activeSendFormScreen: action.payload };

    case LOGOUT:
      return { ...initialState, authLoading: false };

    default:
      return state;
  }
}

export const AppContext = React.createContext<AppContextValue>({
  dispatch: () => {},
  state: initialState,
});

export const useAppState = (): AppContextValue => React.useContext(AppContext);

export type AppStateRouterType = ReturnType<typeof useAppState>;

const AppProvider: React.FC<Props> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  async function initializeUser() {
    // Try to restore from localStorage first
    const storedUser = localStorage.getItem("user");
    const storedBusiness = localStorage.getItem("business");
    const authToken = Cookies.get(token);

    if (storedUser && storedBusiness && authToken) {
      dispatch({
        type: RESTORE_SESSION,
        payload: {
          authToken,
          businessUserRole: getUserRoleFromToken(authToken) as UserRole,
          user: JSON.parse(storedUser),
          business: JSON.parse(storedBusiness),
        },
      });
    } else {
      dispatch({ type: AUTH_LOADING, payload: false });
    }
  }

  useEffect(() => {
    initializeUser();
  }, []);

  return (
    <AppContext.Provider
      value={{
        dispatch,
        state,
      }}
    >
      {state.authLoading ? (
        <AppSkeleton key="skeleton" />
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      )}
    </AppContext.Provider>
  );
};

export default AppProvider;
